/**
 * @file useUserSocket.js
 * @description Lightweight Socket.IO hook for user-level events (no threadId required).
 *
 * Used by pages that need WebSocket events scoped to the user (e.g., KnowledgeManagerPage)
 * but don't have a specific thread context.
 *
 * Uses chatId: 'user-global' so the backend can track the connection without a real threadId.
 * The backend joins the socket to `user:${userId}` room, so emitToUser() reaches it.
 *
 * @module hooks/useUserSocket
 */

import { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import {
  isAccountInactiveCode,
  signalAccountInactive,
  codeFromSocketReason,
} from '../services/accountInactiveSignal';

const SOCKET_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_WS_URL
  : (process.env.REACT_APP_API_URL || 'http://localhost:5678');

/**
 * @returns {{ socket: import('socket.io-client').Socket | null, isConnected: boolean }}
 */
export const useUserSocket = () => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  const { session, loading: loadingAuth } = useAuth();
  const authToken = session?.access_token;

  useEffect(() => {
    if (loadingAuth || !authToken) {
      return;
    }

    console.log('[useUserSocket] Connecting user-global socket');

    const socketInstance = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      auth: { token: authToken },
      query: { chatId: 'user-global' },
      transports: ['websocket', 'polling']
    });

    setSocket(socketInstance);
    socketRef.current = socketInstance;

    const handleConnect = () => {
      console.log(`[useUserSocket] Connected, socketId: ${socketInstance.id}`);
      setIsConnected(true);
    };

    const handleDisconnect = (reason) => {
      console.log(`[useUserSocket] Disconnected: ${reason}`);
      setIsConnected(false);
    };

    const handleConnectError = (err) => {
      // #559: el handshake rechazado por cuenta cortada llega aquí con el código a secas en
      // `err.message`. Sin esto, el usuario se quedaba en el spinner de reconexión infinita
      // (`reconnectionAttempts: Infinity`, arriba) sin saber por qué.
      if (isAccountInactiveCode(err?.message)) {
        console.warn(`[useUserSocket] Cuenta no activa (${err.message}) — llevando a la pantalla explicativa`);
        signalAccountInactive({ code: err.message, source: 'useUserSocket:connect_error' });
        setIsConnected(false);
        return;
      }
      console.warn(`[useUserSocket] Connection error: ${err.message}`);
      setIsConnected(false);
    };

    // #559: aviso a un socket YA conectado, justo antes de que el backend lo cierre
    // (`socketManager.disconnectUser`). Todos los sockets autenticados entran en la room
    // `user:<id>`, así que este canal recibe el aviso aunque el corte lo dispare otra pestaña.
    const handleAccountInactive = (payload) => {
      const code = codeFromSocketReason(payload?.reason);
      console.warn(`[useUserSocket] El backend ha cortado la cuenta (${payload?.reason || 'sin motivo'})`);
      signalAccountInactive({ code, source: 'useUserSocket:account-inactive' });
      setIsConnected(false);
    };

    socketInstance.on('connect', handleConnect);
    socketInstance.on('disconnect', handleDisconnect);
    socketInstance.on('connect_error', handleConnectError);
    socketInstance.on('account-inactive', handleAccountInactive);

    return () => {
      console.log('[useUserSocket] Disconnecting user-global socket');
      socketInstance.off('connect', handleConnect);
      socketInstance.off('disconnect', handleDisconnect);
      socketInstance.off('connect_error', handleConnectError);
      socketInstance.off('account-inactive', handleAccountInactive);
      socketInstance.disconnect();
      setSocket(null);
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [loadingAuth, authToken]);

  // Update token on existing socket without reconnecting
  useEffect(() => {
    if (!authToken || !socketRef.current) return;
    socketRef.current.auth = { token: authToken };
  }, [authToken]);

  return { socket, isConnected };
};

export default useUserSocket;
